import { type License } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useRoleDetailsData } from "@src/appV2/redesign/Roles/RoleDetails/useRoleDetailsData";
import { generatePath, Redirect, useHistory } from "react-router-dom";

import { PageWrapper } from "../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../components/StickyScrollPageHeader";
import { RoleDetailsFooter } from "../../../Roles/RoleDetails/Footer";
import { RoleDetailsPageContent } from "../../../Roles/RoleDetails/PageContent";
import {
  ACCOUNT_ROLE_DETAILS_ADD_LICENSE_FULL_PATH,
  ACCOUNT_ROLE_DETAILS_REMOVE_LICENSE_MODAL_PATH,
  ACCOUNT_ROLE_DETAILS_REMOVE_ROLE_MODAL_PATH,
  ACCOUNT_ROLE_DETAILS_RENEW_LICENSE_MODAL_PATH,
  ACCOUNT_ROLES_FULL_PATH,
} from "../paths";

interface AccountRoleDetailsPageProps {
  qualificationId: string;
}

export function AccountRoleDetailsPage(props: AccountRoleDetailsPageProps) {
  const { qualificationId } = props;

  const {
    isAutoApproved,
    isEmpty,
    isLoading,
    nlcStates,
    qualificationLabel,
    role,
    scrollContainerRef,
    shoppableQualificationId,
    workerCredentialRequirements,
    onNlcStatesClick,
  } = useRoleDetailsData(qualificationId);

  const history = useHistory();

  if (!isLoading && !isDefined(role)) {
    return <Redirect to={ACCOUNT_ROLES_FULL_PATH} />;
  }

  const getTitle = () => {
    if (isLoading) {
      return "Loading...";
    }

    if (isAutoApproved) {
      return qualificationLabel;
    }

    return `${qualificationLabel} Licenses`;
  };

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title={getTitle()}
        headerVariant="tertiary"
        scrollContainerRef={scrollContainerRef}
      />

      <RoleDetailsPageContent
        ref={scrollContainerRef}
        isAutoApproved={isAutoApproved}
        isLoading={isLoading}
        qualificationLabel={qualificationLabel}
        licenses={role?.licenses ?? []}
        workerCredentialRequirements={workerCredentialRequirements}
        nlcStates={nlcStates}
        onNlcStatesClick={onNlcStatesClick}
        onAddLicense={() => {
          history.push(
            generatePath(ACCOUNT_ROLE_DETAILS_ADD_LICENSE_FULL_PATH, {
              qualificationId,
            })
          );
        }}
        onRemoveLicense={async (licenseId: string) => {
          history.replace(
            generatePath(ACCOUNT_ROLE_DETAILS_REMOVE_LICENSE_MODAL_PATH, {
              qualificationId: role?.qualification.id ?? "",
              licenseId,
            })
          );
        }}
        onRenewLicense={async (license: License) => {
          logEvent(APP_V2_USER_EVENTS.CLICKED_LICENSE_RENEWAL_BUTTON, {
            qualification: license.attributes.qualification,
            state: license.attributes.state,
            qualificationId: role?.qualification.id ?? "",
          });
          history.replace(
            generatePath(ACCOUNT_ROLE_DETAILS_RENEW_LICENSE_MODAL_PATH, {
              qualificationId: role?.qualification.id ?? "",
              licenseId: license.id,
            })
          );
        }}
      />

      <RoleDetailsFooter
        qualificationLabel={qualificationLabel}
        onRemoveRole={() => {
          history.replace(
            generatePath(ACCOUNT_ROLE_DETAILS_REMOVE_ROLE_MODAL_PATH, {
              qualificationId,
              shoppableQualificationId,
            })
          );
        }}
        onAddLicense={
          isAutoApproved || isEmpty
            ? undefined
            : () => {
                history.push(
                  generatePath(ACCOUNT_ROLE_DETAILS_ADD_LICENSE_FULL_PATH, {
                    qualificationId,
                  })
                );
              }
        }
      />
    </PageWrapper>
  );
}
